import type { Minor, Quantity, Subtotal } from './money'
import {
  BASIS_POINTS_FULL,
  addMinor,
  applyBasisPoints,
  subMinor,
  subtotalMinor,
  valueOf,
  withoutBasisPoints,
} from './money'

/**
 * What a holding has to carry for its share of the totals to be worked out and
 * said out loud. A holdings row satisfies it; the shape is written here so the
 * arithmetic does not have to reach up into the API for a type.
 */
export interface AttributableHolding {
  accountId: number
  /** The subject's share of the account, in basis points. */
  shareBp: number
  quantity: Quantity
  priceMinor: Minor | null
  priceCurrency: string | null
  /** The whole position's value in the base currency, before the share. */
  valueMinor: Minor | null
  haircutBp: number
}

export interface AttributedHolding<T> {
  holding: T
  /** Null where the position has no value at all, which is not the same as none. */
  attributedMinor: Minor | null
}

/**
 * Each holding's contribution to the look-through total.
 *
 * The share is applied per account and not per holding, because that is where
 * the totals apply it: the rollup sums an account's positions and rounds the
 * share once, so a column that rounded each row on its own would add up to a
 * few øre either side of the headline it sits under. Running the account's
 * subtotal and taking the difference gives every row a figure within one minor
 * unit of its own share, and gives the column a sum that is the headline
 * exactly.
 */
export function attributeHoldings<T extends AttributableHolding>(
  holdings: readonly T[],
): AttributedHolding<T>[] {
  const running = new Map<number, { grossMinor: Minor; attributedMinor: Minor }>()

  return holdings.map((holding) => {
    if (holding.valueMinor === null) return { holding, attributedMinor: null }

    const before = running.get(holding.accountId) ?? { grossMinor: 0, attributedMinor: 0 }
    const grossMinor = addMinor(before.grossMinor, holding.valueMinor)
    const attributedMinor = applyBasisPoints(grossMinor, holding.shareBp)
    running.set(holding.accountId, { grossMinor, attributedMinor })

    return { holding, attributedMinor: subMinor(attributedMinor, before.attributedMinor) }
  })
}

/**
 * What the attributed column comes to, and how many rows it cannot speak for.
 *
 * A row with no value is left out rather than added in as zero, which is what
 * the snapshot totals this column is checked against already do.
 */
export function attributedTotal(
  attributed: readonly AttributedHolding<unknown>[],
): Subtotal {
  return subtotalMinor(attributed.map((row) => row.attributedMinor))
}

/**
 * Why a holding counts for something other than quantity × price. Every field
 * is a fact drawn from the holding itself, and absent where it does not apply —
 * a reason that is always given is one nobody reads.
 */
export interface ValueReasons {
  /** The subject's share of the account, where the account is not wholly theirs. */
  shareBp: number | null
  /** The discount the valuation carries, where it carries one. */
  haircutBp: number | null
  /** The currency the price is quoted in, where the totals are in another. */
  priceCurrency: string | null
  /**
   * True where the holding's own numbers do not multiply out to the value the
   * totals used. The discount on private holdings is set for the household
   * rather than for the valuation, so it is taken off the value without being
   * recorded against the holding — leaving a difference that would otherwise
   * go unmentioned under an explanation claiming to be complete.
   */
  discountedFurther: boolean
}

export function valueReasons(
  holding: AttributableHolding,
  baseCurrency: string,
): ValueReasons {
  return {
    shareBp: holding.shareBp < BASIS_POINTS_FULL ? holding.shareBp : null,
    haircutBp: holding.haircutBp > 0 ? holding.haircutBp : null,
    priceCurrency:
      holding.priceCurrency !== null && holding.priceCurrency !== baseCurrency
        ? holding.priceCurrency
        : null,
    discountedFurther: multipliesOut(holding, baseCurrency) === false,
  }
}

export function hasReasons(reasons: ValueReasons): boolean {
  return (
    reasons.shareBp !== null ||
    reasons.haircutBp !== null ||
    reasons.priceCurrency !== null ||
    reasons.discountedFurther
  )
}

/**
 * Whether quantity, price and the holding's own haircut come to the value the
 * totals used — and undefined where the holding does not carry enough to say.
 * A price in another currency was converted at a rate the holding does not
 * record, so nothing can be concluded from a figure that fails to match.
 */
function multipliesOut(holding: AttributableHolding, baseCurrency: string): boolean | undefined {
  if (holding.valueMinor === null || holding.priceMinor === null) return undefined
  if (holding.priceCurrency !== baseCurrency) return undefined
  return (
    valueOf(withoutBasisPoints(holding.quantity, holding.haircutBp), holding.priceMinor) ===
    holding.valueMinor
  )
}
